import React from "react";
import { Trash2, Send, BadgeCheck, ClipboardCheck } from "lucide-react";
import LoadingButton from "./LoadingButton";
import {
  isQaQcManager,
  isInspector,
} from "../../../../../constants/projectRoles";

const statusLabel = (s) => (s || "-").toString();
const isDraftLike = (s) => (s || "").toLowerCase() === "draft";

const canSubmit = (status) => isDraftLike(status);
const canApprove = (status, projectRole) =>
  (status || "").toLowerCase() === "submitted" && isQaQcManager(projectRole);

const canReject = (status, projectRole) =>
  (status || "").toLowerCase() === "submitted" && isQaQcManager(projectRole);
const canReopen = (status, projectRole) => {
  const st = (status || "").toLowerCase();
  return (
    st === "rejected" &&
    (isInspector(projectRole) || isQaQcManager(projectRole))
  );
};

const InspectionsListTable = ({
  inspections,
  loading,
  error,
  projectRole,
  canDeleteInspection,
  rowLoading,
  onOpen,
  onDelete,
  onSubmit,
  onApprove,
  onRejectPrompt,
  onReopen,
  onAssignInspection,
}) => {
  if (loading)
    return <div className="p-4 text-sm text-gray-500">Loading...</div>;

  if (error) {
    return (
      <div className="p-4 text-sm text-red-600">
        Failed to load inspections.
      </div>
    );
  }

  if (!inspections || inspections.length === 0) {
    return <div className="p-4 text-sm text-gray-500">No inspections yet.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-700">
          <tr>
            <th className="text-left p-3">Inspection Number</th>
            <th className="text-left p-3">Date</th>
            <th className="text-left p-3">Created by</th>
            <th className="text-left p-3">Type</th>
            {/* <th className="text-left p-3">Location</th> */}
            <th className="text-left p-3">Site location</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3">Findings</th>
            <th className="text-left p-3">Critical</th>
            <th className="text-left p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {inspections.map((i) => {
            const showSubmit = canSubmit(i.status);
            const showApprove = canApprove(i.status, projectRole);
            const showReject = canReject(i.status, projectRole);
            const showReopen = canReopen(i.status, projectRole);
            const rl = rowLoading?.[i.id] || {};
            const rowBusy = !!(
              rl.submit ||
              rl.approve ||
              rl.reject ||
              rl.reopen
            );

            return (
              <tr
                key={i.id}
                className="border-t cursor-pointer hover:bg-gray-50"
                onClick={(event) => {
                  if (event.target.closest("button, a, input, select, textarea, [role='button']")) {
                    return;
                  }
                  onOpen(i.id);
                }}
              >
                <td className="p-3 font-medium">
                  {i.inspection_number || "—"}
                </td>
                <td className="p-3">{i.inspection_date || "-"}</td>
                <td className="p-3">
                  {`${i.inspector?.first_name || ""} ${i.inspector?.last_name || ""}`.trim() ||
                    "-"}
                </td>
                <td className="p-3">{i.inspection_type || "-"}</td>
                {/* <td className="p-3">{i.location || "-"}</td> */}
                <td className="p-3">{i.site_location || i.location}</td>
                <td className="p-3">{statusLabel(i.status)}</td>
                <td className="p-3">{i.findings_count ?? 0}</td>
                <td className="p-3">{i.critical_issues_count ?? 0}</td>
                <td className="p-3">
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => onOpen(i.id)}
                      className="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs"
                    >
                      Open
                    </button>

                    {onAssignInspection && (
                      <button
                        type="button"
                        onClick={() => onAssignInspection(i)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 text-xs"
                      >
                        <ClipboardCheck className="w-3.5 h-3.5" /> Assign
                        Inspection
                      </button>
                    )}

                    {canDeleteInspection && (
                      <button
                        type="button"
                        onClick={() => onDelete(i.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete inspection"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}

                    {showSubmit && (
                      <LoadingButton
                        loading={!!rl.submit}
                        loadingText="Submitting…"
                        disabled={rowBusy}
                        spinnerSize="small"
                        onClick={() => onSubmit(i.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-black text-xs"
                      >
                        <span className="inline-flex items-center gap-1">
                          <Send className="w-3.5 h-3.5" /> Submit
                        </span>
                      </LoadingButton>
                    )}

                    {showApprove && (
                      <LoadingButton
                        loading={!!rl.approve}
                        loadingText="Approving…"
                        disabled={rowBusy}
                        spinnerSize="small"
                        onClick={() => onApprove(i.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-xs"
                      >
                        <span className="inline-flex items-center gap-1">
                          <BadgeCheck className="w-3.5 h-3.5" /> Approve
                        </span>
                      </LoadingButton>
                    )}

                    {showReject && (
                      <LoadingButton
                        loading={!!rl.reject}
                        loadingText="Rejecting…"
                        disabled={rowBusy}
                        spinnerSize="small"
                        onClick={() => onRejectPrompt(i.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 text-xs"
                      >
                        Reject
                      </LoadingButton>
                    )}

                    {showReopen && (
                      <LoadingButton
                        loading={!!rl.reopen}
                        loadingText="Reopening…"
                        disabled={rowBusy}
                        spinnerSize="small"
                        onClick={() => onReopen(i.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-yellow-600 text-white hover:bg-yellow-700 text-xs"
                      >
                        Reopen
                      </LoadingButton>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(InspectionsListTable);
